import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import queryString from 'query-string';
import { deepEqual } from 'fast-equals';

import { HYDRA_PUSH_NOTIFICATIONS, HYDRA_VIP_DASHBOARD } from 'constants/hydraTests';

import {
  fetchAccountInfo,
  fetchGiftCardBalance,
  redeemGiftCard,
  resetCancelOrReturnItems,
  updateGiftCardRedeemCode
} from 'actions/account/account';
import { firePixelServer } from 'actions/pixelServer';
import { fetchOrders } from 'actions/account/orders';
import { resetReturnsInState } from 'actions/account/returns';
import { fetchRewardsInfo } from 'actions/account/rewards';
import { fetchPaymentInfo } from 'actions/account/payment';
import { fetchShippingInfo } from 'actions/account/shipping';
import { checkCustomerAuthentication } from 'actions/authentication';
import { fetchRecos } from 'actions/recos';
import AccountHeader from 'containers/account/AccountHeader';
import AccountNavList from 'components/account/AccountNavList';
import RecentOrder from 'components/account/RecentOrder';
import PrimaryAccountInfo from 'components/account/PrimaryAccountInfo/PrimaryAccountInfo';
import OrderHistoryOverview from 'components/account/OrderHistoryOverview/OrderHistoryOverview';
import ReturnsAndExchanges from 'components/account/ReturnsAndExchanges';
import Wishes from 'components/account/Wishes';
import SiteAwareMetadata from 'components/SiteAwareMetadata';
import { PageLoader } from 'components/Loader';
import Notifications from 'components/Notifications';
import MelodyCarousel from 'components/common/MelodyCarousel';
import MelodyCardProduct from 'components/common/melodyCard/MelodyCardProduct';
import { isAssigned, triggerAssignment } from 'actions/ab';
import marketplace from 'cfg/marketplace.json';
import css from 'styles/containers/account/account.scss';

const {
  account: { showRecos, janusRecoSlot, hideEmailSubscriptionLink, hideFavoritesLink, hasGiftCards },
  features: { showAccountRewards },
  search: { msaImageParams }
} = marketplace;

export class Account extends Component {
  static beforeFetchDataOnServer({ dispatch }) {
    return dispatch(triggerAssignment(HYDRA_VIP_DASHBOARD));
  }

  constructor() {
    super();
    this.ordersRef = createRef();
  }

  componentDidMount() {
    const { firePixelServer, checkCustomerAuthentication, triggerAssignment } = this.props;
    triggerAssignment(HYDRA_PUSH_NOTIFICATIONS);
    const returnTo = '/account';
    checkCustomerAuthentication(returnTo, () => this.fetchData());
    firePixelServer('my-account');
  }

  componentDidUpdate(prevProps) {
    const { location: { search: prevSearch }, fetchOrders, firePixelServer } = prevProps;
    const { location: { search: nextSearch } } = this.props;
    const prevParams = queryString.parse(prevSearch);
    const nextParams = queryString.parse(nextSearch);

    // Fetch new orders when query params update
    // Only Order History component is responsible for updating query params (page, pageSize, range)
    if (!deepEqual(prevParams, nextParams)) {
      fetchOrders(nextParams, '/account');
      firePixelServer('my-account');
    }
  }

  fetchData() {
    const {
      location: { search },
      account: { customerInfo, giftCardBalance },
      payment: { paymentInstruments },
      returns: { step: returnsStep },
      shipping: { addresses },
      rewards: { rewardsInfo },
      recos: { janus },
      fetchOrders,
      fetchPaymentInfo,
      fetchShippingInfo,
      resetCancelOrReturnItems,
      resetReturnsInState,
      fetchAccountInfo,
      fetchGiftCardBalance,
      fetchRewardsInfo,
      fetchRecos,
      showAccountRewards,
      showRecos
    } = this.props;

    const queryParams = queryString.parse(search);
    const returnTo = '/account';

    // Fetch customer orders
    // If query params are not specified, it defaults to page = 1, pageSize = 10, range = 'all'
    fetchOrders(queryParams, returnTo);

    // Fetch customer account information
    if (!customerInfo) {
      fetchAccountInfo(returnTo);
    }
    // Fetch all payment insruments
    if (!paymentInstruments) {
      fetchPaymentInfo(null, returnTo);
    }
    // Fetch shipping addresses
    if (!addresses) {
      fetchShippingInfo(null, returnTo);
    }
    // Fetch gift card balance
    // Check for null since 0 is a valid GC balance
    if (hasGiftCards && giftCardBalance === null) {
      fetchGiftCardBalance(returnTo);
    }
    // Fetch Zappos Rewards information (client-side only)
    if (showAccountRewards && !rewardsInfo) {
      fetchRewardsInfo();
    }

    if (returnsStep && returnsStep !== 'SELECT_RETURN_ITEMS') {
      resetCancelOrReturnItems();
      resetReturnsInState();
    }

    if (showRecos && (!janus?.[janusRecoSlot])) {
      fetchRecos({ widget: janusRecoSlot, limit: '20' });
    }
  }

  handleGiftCardRedeemSubmit = event => {
    event.preventDefault();
    const { redeemGiftCard, account: { giftCardRedeemCode } } = this.props;
    // Pass along the DOM node in order to focus() input field on successful/unsuccessful submission
    const inputElement = event.target.claimCode;
    redeemGiftCard(giftCardRedeemCode, inputElement);
  };

  handleUpdateGiftCardRedeemCode = event => {
    const { updateGiftCardRedeemCode } = this.props;
    const { value } = event.target;
    updateGiftCardRedeemCode(value);
  };

  handleRewardsEnrollClick = () => {
    this.context.router.pushPreserveAppRoot('/rewards/dashboard');
  };

  makeAccountHeader = () => {
    const { account: { customerInfo: { name } } } = this.props;
    return (
      <div className={css.accountHeader}>
        <div className={css.desktop}>
          <AccountHeader title={`Hello, ${name}!`}/>
        </div>
        <div className={css.mobile}>
          <AccountHeader title="My Account"/>
        </div>
      </div>
    );
  };

  makePrimaryAccountInfo = () => {
    const { account, isVIPDashboard, payment, rewards, shipping, showAccountRewards } = this.props;
    const { paymentInstruments } = payment;
    const { addresses } = shipping;

    // Fallback to first payment and shipping address if a primary one hasn't been set
    const primaryPayment = paymentInstruments.find(instrument => instrument.primary) || paymentInstruments[0];
    const primaryAddress = addresses.find(address => address.isDefaultShippingAddress) || addresses[0];

    return (
      <div className={css.primaryAccountInfo}>
        <PrimaryAccountInfo
          account={account}
          isVIPDashboard={isVIPDashboard}
          payment={primaryPayment}
          address={primaryAddress}
          rewards={rewards}
          showAccountRewards={showAccountRewards}
          onUpdateGiftCardRedeemCode={this.handleUpdateGiftCardRedeemCode}
          onGiftCardRedeemSubmit={this.handleGiftCardRedeemSubmit}
          onRewardsEnrollClick={this.handleRewardsEnrollClick}/>
      </div>
    );
  };

  makeOrderHistory = () => {
    const { location, orders: { totalPages, orders, isLoading } } = this.props;
    return (
      <div className={css.desktopOrderHistory} ref={this.ordersRef}>
        <h2>Your Order History</h2>
        <OrderHistoryOverview
          location={location}
          orders={orders}
          totalPages={totalPages}
          isLoading={isLoading}
          onFetchOrders={fetchOrders}/>
      </div>
    );
  };

  makeReturnsAndExchanges = () => (
    <div className={css.returnsAndExchanges}>
      <ReturnsAndExchanges/>
    </div>
  );

  makeRecentOrder = () => {
    const { orders: { orders } } = this.props;
    if (orders?.length) {
      return (
        <div className={css.recentOrder}>
          <RecentOrder order={orders[0]}/>
        </div>
      );
    }
  };

  makeAccountNavList = showAccountRewards => (
    <div className={css.accountNavList}>
      <AccountNavList showAccountRewards={showAccountRewards} hideFavoritesLink={hideFavoritesLink} hideEmailSubscriptionLink={hideEmailSubscriptionLink}/>
    </div>
  );

  makeRecos = () => {
    const { showRecos, recos } = this.props;
    if (showRecos && recos.janus?.[janusRecoSlot]?.recos?.length) {
      const janusRecoData = recos.janus[janusRecoSlot];
      const { title } = janusRecoData;
      return (
        <div className={css.recos}>
          <MelodyCarousel title={title || 'More Items Picked For You!'}>
            {janusRecoData.recos.map((reco, index) => <MelodyCardProduct
              msaImageParams={msaImageParams}
              cardData={reco}
              componentStyle="recommender"
              eventLabel="Recommender"
              melodyCardTestId="recommenderProduct"
              key={index} />)}
          </MelodyCarousel>
        </div>
      );
    }
  };

  render() {
    const {
      orders: { orders },
      shipping: { addresses },
      payment: { paymentInstruments },
      account: { customerInfo, giftCardBalance },
      hydraPushNotifications,
      rewards: { rewardsInfo }
    } = this.props;

    const { marketplace: { account: { isWishesEnabled } } } = this.context;

    if (!orders || !addresses || !paymentInstruments || !customerInfo || (giftCardBalance === null && hasGiftCards)) {
      return <PageLoader centered={true}/>;
    }

    const { enrolled, consented, canEnroll } = rewardsInfo || {};
    const showRewards = showAccountRewards && ((enrolled && consented) || canEnroll);

    return (
      <SiteAwareMetadata>
        <div className={css.account}>
          {this.makeAccountHeader()}       {/* Both */}
          {isWishesEnabled && <Wishes />}
          {this.makePrimaryAccountInfo()}  {/* Desktop */}
          {this.makeOrderHistory()}        {/* Desktop */}
          {hydraPushNotifications && <Notifications subscriptionName="Returns" value="Subscribe to Return Notifications" />}
          {this.makeReturnsAndExchanges()} {/* Desktop */}
          {this.makeRecentOrder()}         {/* Mobile */}
          {this.makeAccountNavList(showRewards)}      {/* Mobile */}
          {this.makeRecos(showRecos)}               {/* Both (6pm only) */}
        </div>
      </SiteAwareMetadata>
    );
  }
}

Account.contextTypes = {
  router: PropTypes.object.isRequired,
  testId: PropTypes.func,
  marketplace: PropTypes.object
};

const mapStateToProps = state => ({
  account: state.account,
  isVIPDashboard: isAssigned(HYDRA_VIP_DASHBOARD, 1, state),
  orders: state.orders,
  payment: state.payment,
  shipping: state.shipping,
  returns: state.returns,
  rewards: state.rewards,
  recos: state.recos,
  showAccountRewards,
  showRecos,
  hydraPushNotifications: isAssigned(HYDRA_PUSH_NOTIFICATIONS, 1, state)
});

export default connect(mapStateToProps, {
  fetchAccountInfo,
  fetchOrders,
  fetchPaymentInfo,
  fetchShippingInfo,
  resetCancelOrReturnItems,
  fetchGiftCardBalance,
  fetchRewardsInfo,
  redeemGiftCard,
  updateGiftCardRedeemCode,
  triggerAssignment,
  firePixelServer,
  checkCustomerAuthentication,
  fetchRecos,
  resetReturnsInState
})(Account);
